/**
 * Formata um número de telefone brasileiro nos padrões nacional ou internacional.
 *
 * @remarks
 * Suporta números com ou sem código de país (+55), com 10 ou 11 dígitos locais.
 * Números com 9 dígitos no número local (após DDD) são formatados como celular
 * (XXXXX-XXXX); números com 8 dígitos são formatados como fixo (XXXX-XXXX).
 * Valores que não se encaixam no padrão brasileiro são retornados sem alteração.
 * O campo `whatsapp` não deve ser formatado por esta função, pois é utilizado
 * em URLs (`wa.me`) e possui semântica distinta.
 *
 * @param value - O número de telefone bruto (ex: "+551151816167", "11988889999").
 * @returns O número formatado ou o valor original se não for reconhecido.
 *
 * @example
 * ```typescript
 * formatPhoneNumber("+551151816167")   // "+55 (11) 5181-6167"
 * formatPhoneNumber("5511988889999")   // "+55 (11) 98888-9999"
 * formatPhoneNumber("1151816167")      // "(11) 5181-6167"
 * formatPhoneNumber("11988889999")     // "(11) 98888-9999"
 * formatPhoneNumber("")                // ""
 * formatPhoneNumber("abc")             // "abc"
 * ```
 */
export function formatPhoneNumber(value: string): string {
  if (!value) {
    return "";
  }

  const digits = value.replace(/\D/g, "");

  if (!digits) {
    return value;
  }

  let hasCountryCode = false;
  let localDigits: string;

  if (digits.length >= 12 && digits.startsWith("55")) {
    hasCountryCode = true;
    localDigits = digits.slice(2);
  } else {
    localDigits = digits;
  }

  if (localDigits.length < 10 || localDigits.length > 11) {
    return value;
  }

  const ddd = localDigits.slice(0, 2);
  const number = localDigits.slice(2);

  let formattedNumber: string;

  if (number.length === 9) {
    formattedNumber = `${number.slice(0, 5)}-${number.slice(5)}`;
  } else if (number.length === 8) {
    formattedNumber = `${number.slice(0, 4)}-${number.slice(4)}`;
  } else {
    return value;
  }

  if (hasCountryCode) {
    return `+55 (${ddd}) ${formattedNumber}`;
  }

  return `(${ddd}) ${formattedNumber}`;
}
